import React, { useEffect, useState, useCallback } from "react";
import "../Styling/EmployeeGrowthTrend.css";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { TrendingUp } from "lucide-react";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
    BarController,      // 👈 ADD THIS
  LineController, 
} from "chart.js";

import { Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
    BarController,      // 👈 ADD THIS
  LineController, 
  Tooltip,
  Legend
);

const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const EmployeeGrowthTrend = ({ filters = {} }) => {
  const { user } = useAuth();

  const [mode, setMode] = useState("rolling");
const [rollingMonths, setRollingMonths] = useState(12); // default 12

  const [year, setYear] = useState(new Date().getFullYear());
  const [labels, setLabels] = useState(months);

  const [monthlyHires, setMonthlyHires] = useState(Array(12).fill(0));
  const [cumulativeGrowth, setCumulativeGrowth] = useState(Array(12).fill(0));

  const [showMonthly, setShowMonthly] = useState(true);
  const [showCumulative, setShowCumulative] = useState(true); // default visible

  const [loading, setLoading] = useState(false);
const [statusFilter, setStatusFilter] = useState("active"); 
// values: "all" | "active" | "inactive"

  const orgId = filters.orgId || user?.organisationId;
  const currentYear = new Date().getFullYear();

  const fillMissingWithPrevious = (arr) => {
    const filled = [...arr];
    for (let i = 0; i < filled.length; i++) {
      if (filled[i] == null) {
        filled[i] = i === 0 ? 0 : filled[i - 1];
      }
    }
    return filled;
  };

  const fetchGrowth = useCallback(async () => {
    if (!orgId) return;

    try {
      setLoading(true);

     let url = `${Base_URL}/Resource/EmployeeGrowth/${orgId}`;

if (statusFilter === "active") {
  url += "?active=true";
} else if (statusFilter === "inactive") {
  url += "?active=false";
}

const res = await fetch(url);


      const result = await res.json();

      const validData = result.filter(
        item => item.year <= currentYear
      );

      const monthlyMap = {};
      const cumulativeMap = {};

      validData.forEach(item => {
        monthlyMap[`${item.year}-${item.month}`] = item.monthlyCount;
        cumulativeMap[`${item.year}-${item.month}`] = item.count;
      });

if (mode === "rolling") {
  const today = new Date();
  const hires = [];
  const cumulative = [];
  const dynamicLabels = [];

  let previousCumulative = 0;

  for (let i = rollingMonths - 1; i >= 0; i--) {
    const d = new Date(
      today.getFullYear(),
      today.getMonth() - i,
      1
    );

    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;

    const isFuture =
      d.getFullYear() > today.getFullYear() ||
      (d.getFullYear() === today.getFullYear() &&
        d.getMonth() > today.getMonth());

    if (isFuture) {
      hires.push(null);
      cumulative.push(null);
    } else {
      const monthlyValue = monthlyMap[key] ?? 0;
      const cumulativeValue =
        cumulativeMap[key] ?? previousCumulative;

      hires.push(monthlyValue);
      cumulative.push(cumulativeValue);

      previousCumulative = cumulativeValue;
    }

    dynamicLabels.push(
      d.toLocaleString("default", { month: "short" })
    );
  }

  setMonthlyHires(hires);
  setCumulativeGrowth(cumulative);
  setLabels(dynamicLabels);
}



      if (mode === "year") {
  const today = new Date();
  const hires = [];
  const cumulative = [];
  let previousCumulative = 0;

  for (let m = 1; m <= 12; m++) {
    const isFuture =
      year > today.getFullYear() ||
      (year === today.getFullYear() && m - 1 > today.getMonth());

    const key = `${year}-${m}`;

    if (isFuture) {
      hires.push(null);
      cumulative.push(null);
    } else {
      const monthlyValue = monthlyMap[key] ?? 0;
      const cumulativeValue =
        cumulativeMap[key] ?? previousCumulative;

      hires.push(monthlyValue);
      cumulative.push(cumulativeValue);

      previousCumulative = cumulativeValue;
    }
  }

  setMonthlyHires(hires);
  setCumulativeGrowth(cumulative);
  setLabels(months);
}

    } catch (err) {
      console.error("Growth API Error:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId, mode, year, currentYear, rollingMonths,statusFilter]);

  useEffect(() => {
    fetchGrowth();
  }, [fetchGrowth]);

  const years = [];
  for (let i = currentYear; i >= currentYear - 5; i--) {
    years.push(i);
  }

  const datasets = [];

  if (showMonthly) {
    datasets.push({
      type: "bar",
      label: "Monthly Hires",
      data: monthlyHires,
      backgroundColor: "rgba(99,102,241,0.5)",
      borderRadius: 6,
      yAxisID: "y"
    });
  }

  if (showCumulative) {
    datasets.push({
      type: "line",
      label: "Cumulative Growth",
      data: cumulativeGrowth,
      borderColor: "#10b981",
      backgroundColor: "#10b981",
      tension: 0.4,
      fill: false,
      yAxisID: "y1"
    });
  }

  const data = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: { position: "top" } },
    scales: {
      y: {
        beginAtZero: true,
        position: "left",
        title: { display: true, text: "Monthly Hires" }
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: { drawOnChartArea: false },
        title: { display: true, text: "Total Employees" }
      }
    }
  };

  return (
    <div className="growth-card">
      <div className="growth-header">
        <div className="header-left">
          <div className="growth-icon">
            <TrendingUp size={18} />
          </div>
          <h6>Employee Growth</h6>
        </div>
 

        <div style={{ display: "flex", gap: "8px" }}>
               <select
  value={statusFilter}
  onChange={(e) => setStatusFilter(e.target.value)}
>
  <option value="all">All Employees</option>
  <option value="active">Active</option>
  <option value="inactive">Inactive</option>
</select>
         <select
  value={mode === "rolling" ? rollingMonths : "year"}
  onChange={(e) => {
    const value = e.target.value;

    if (value === "year") {
      setMode("year");
    } else {
      setMode("rolling");
      setRollingMonths(Number(value));
    }
  }}
>
  <option value="3">Past 3 Months</option>
  <option value="6">Past 6 Months</option>
  <option value="12">Past 12 Months</option>
  <option value="year">By Year</option>
</select>


          {mode === "year" && (
  <select value={year} onChange={e => setYear(Number(e.target.value))}>
    {years.map(y => (
      <option key={y} value={y}>{y}</option>
    ))}
  </select>
)}

        </div>
      </div>

      {/* Toggle Section */}
      <div style={{ marginBottom: "12px", display: "flex", gap: "12px" }}>
        <label>
          <input
            type="checkbox"
            checked={showCumulative}
            onChange={() => setShowCumulative(prev => !prev)}
          />
          Cumulative
        </label>

        <label>
          <input
            type="checkbox"
            checked={showMonthly}
            onChange={() => setShowMonthly(prev => !prev)}
          />
          Monthly Hires
        </label>
      </div>

      <div className="growth-chart">
        {loading ? <p>Loading...</p> : <Chart type="bar" data={data} options={options} />}
      </div>
    </div>
  );
};

export default EmployeeGrowthTrend;
